// file: src/Overview.jsx
// quick build marker – remove later

import React, { useEffect, useRef, useState } from "react";
import {
  lookup,
  listDoNotTow,
  searchFacilityTitles,
} from "../api/towingApi.js";
import Loader from "../components/Loader.js";
import TowingImage from "../Images/TowingImage1.png";
import { motion, AnimatePresence } from "framer-motion";
import Sidebar from "../TowingDashboard/Sidebar.jsx";

/* Brand */
const BLUE = "#2A7EFA";
const BLUE_LIGHT = "#5AA9FF";

function clsx(...p) {
  return p.filter(Boolean).join(" ");
}

/* Utilities */
function fmtPartsTZ(iso, timeZone) {
  try {
    const d = iso instanceof Date ? iso : parseIsoSafe(iso); // adds Z if needed + validates

    if (!d) throw new Error("Invalid date");

    return {
      date: d.toLocaleDateString(undefined, {
        timeZone: timeZone || undefined,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }),
      time: d.toLocaleTimeString(undefined, {
        timeZone: timeZone || undefined,
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  } catch {
    return { date: iso || "", time: "" };
  }
}

function parseIsoSafe(s) {
  if (!s) return null;
  if (s instanceof Date) return Number.isNaN(s.getTime()) ? null : s;

  // already has "Z" or explicit offset?
  if (/[zZ]$/.test(s) || /[-+]\d{2}:\d{2}$/.test(s)) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  // add Z if it's a bare "YYYY-MM-DDTHH:MM(:SS)"
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(s)) {
    const d = new Date(s + "Z");
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function normalizePlate(s) {
  return String(s || "")
    .toUpperCase()
    .replace(/\s+/g, "")
    .slice(0, 24);
}
function normalizeFacility(s) {
  return String(s || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}
function toDateFromDnt(dateStr, timeStr) {
  const t = String(timeStr || "").match(/^\d{2}:\d{2}(:\d{2})?$/)
    ? timeStr.length === 5
      ? `${timeStr}:00`
      : timeStr
    : "00:00:00";

  // IMPORTANT: no trailing 'Z' here
  const localIso = `${String(dateStr).slice(0, 10)}T${t}`;
  const d = new Date(localIso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dntRange(dnt) {
  const s = parseIsoSafe(dnt?.valid_from_utc);
  const e = parseIsoSafe(dnt?.valid_until_utc);
  if (dnt?.is_permanent) {
    return {
      s: s || toDateFromDnt(dnt?.start_date, dnt?.start_time),
      e: null,
      permanent: true,
    };
  }
  if (s && e) return { s, e, permanent: false };

  return {
    s: toDateFromDnt(dnt?.start_date, dnt?.start_time),
    e: toDateFromDnt(dnt?.end_date, dnt?.end_time),
    permanent: false,
  };
}

function overlap(aStart, aEnd, bStart, bEnd) {
  // Treat touching endpoints as NOT overlapping:
  // [aStart, aEnd) and [bStart, bEnd)
  if (!aStart || !aEnd || !bStart || !bEnd) return false;
  return aStart < bEnd && bStart < aEnd;
}

/* Small components */
function PlatePillMissing() {
  return (
    <div
      className="inline-flex max-w-full items-center gap-2 rounded-full px-3 py-1 text-white whitespace-nowrap leading-none"
      style={{
        background:
          "linear-gradient(135deg, rgba(244,63,94,1), rgba(225,29,72,1))",
        boxShadow: "0 8px 20px rgba(225,29,72,0.22)",
      }}
      aria-label="License plate not listed"
      title="License plate not listed"
    >
      <svg
        viewBox="0 0 24 24"
        className="h-4 w-4 shrink-0"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        aria-hidden
      >
        <circle cx="12" cy="12" r="8" />
        <path d="M8.5 8.5l7 7" />
      </svg>
      <span
        className="font-medium tracking-wide"
        style={{ fontSize: "clamp(12px, 1.5vw, 14px)" }}
      >
        License plate not listed
      </span>
    </div>
  );
}

/* Small spinner */
function Spinner({ className = "" }) {
  return (
    <svg
      className={clsx("h-5 w-5 animate-spin", className)}
      viewBox="0 0 24 24"
      fill="none"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
      />
    </svg>
  );
}

function getViewerTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

const END_GRACE_MS = 2 * 60 * 60 * 1000;

function statusWithGrace(endDate) {
  if (!endDate) return "pending";
  const now = Date.now();
  const end = endDate.getTime();
  if (now <= end) return "active";
  if (now <= end + END_GRACE_MS) return "expired"; // "recently ended"
  return "hidden";
}

/**
 * Build a Date (UTC instant) that corresponds to the given wall-clock
 * time in a specific IANA timezone.
 * This uses an Intl "round-trip" correction to handle offsets/DST.
 */
function makeDateInTimeZone(tz, y, m, d, hh = 0, mm = 0, ss = 0) {
  // First guess: interpret the wall-clock components as if they were UTC.
  const guess = new Date(Date.UTC(y, m - 1, d, hh, mm, ss));

  // See what wall-clock time that guess corresponds to in the timezone.
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(guess);

  const map = Object.fromEntries(
    parts.filter((p) => p.type !== "literal").map((p) => [p.type, p.value]),
  );

  const asIfUTC = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour),
    Number(map.minute),
    Number(map.second),
  );

  // Offset correction: move guess so that when formatted in tz it matches (y,m,d,hh,mm,ss)
  const diff = asIfUTC - guess.getTime();
  return new Date(guess.getTime() - diff);
}

/**
 * Returns { start, end } as UTC instants representing today's [start, end)
 * in the given timezone (handles DST by computing next local midnight correctly).
 */
function todayRangeInTimeZone(tz) {
  const now = new Date();

  // Get today's Y/M/D in tz
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);

  const m = Object.fromEntries(
    parts.filter((p) => p.type !== "literal").map((p) => [p.type, p.value]),
  );

  const y = Number(m.year);
  const mo = Number(m.month);
  const d = Number(m.day);

  const start = makeDateInTimeZone(tz, y, mo, d, 0, 0, 0);

  // To get "next local midnight" safely across DST:
  // jump forward ~36h and re-read local Y/M/D, then build midnight for that date.
  const plus36h = new Date(start.getTime() + 36 * 60 * 60 * 1000);
  const parts2 = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(plus36h);

  const m2 = Object.fromEntries(
    parts2.filter((p) => p.type !== "literal").map((p) => [p.type, p.value]),
  );

  const end = makeDateInTimeZone(
    tz,
    Number(m2.year),
    Number(m2.month),
    Number(m2.day),
    0,
    0,
    0,
  );

  return { start, end };
}

function parseReservationInstant(iso, facilityTz = "UTC") {
  if (!iso) return null;
  if (iso instanceof Date) return Number.isNaN(iso.getTime()) ? null : iso;

  const s = String(iso);

  // Has explicit timezone? Parse normally.
  if (/[zZ]$/.test(s) || /[-+]\d{2}:\d{2}$/.test(s)) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  // No timezone => treat as facility local wall time.
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    const hh = Number(m[4]);
    const mm = Number(m[5]);
    const ss = Number(m[6] || 0);
    return makeDateInTimeZone(facilityTz || "UTC", y, mo, d, hh, mm, ss);
  }

  // Fallback
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}
/* Badges & pills */
function StatusBadge({ status }) {
  const map = {
    active: {
      bg: "bg-emerald-50",
      text: "text-emerald-700",
      dot: "bg-emerald-500",
    },
    expired: { bg: "bg-red-50", text: "text-red-600", dot: "bg-red-400" },
    pending: { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
  };
  const s = map[status] || map.pending;
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium leading-none",
        s.bg,
        s.text,
      )}
    >
      <span className={clsx("h-2 w-2 rounded-full", s.dot)} />{" "}
      {status[0].toUpperCase() + status.slice(1)}
    </span>
  );
}

function emitSidebarImagesLoading(isLoading) {
  try {
    window.dispatchEvent(
      new CustomEvent("towing:sidebar:images-loading", { detail: !!isLoading }),
    );
  } catch {}
}

// already present in your file:
function emitFacilityImages(payload) {
  try {
    window.dispatchEvent(
      new CustomEvent("towing:facility-images", { detail: payload || null }),
    );
  } catch {}
}
function emitSidebarPlate(plateOrNull) {
  try {
    window.dispatchEvent(
      new CustomEvent("towing:sidebar:plate", {
        detail: plateOrNull ? String(plateOrNull).toUpperCase() : "",
      }),
    );
  } catch {}
}

function PlatePillSmall({ plate }) {
  if (!plate) return null;
  return (
    <div
      className="inline-flex max-w-full items-center gap-2 rounded-full px-3 py-1 text-white whitespace-nowrap leading-none"
      style={{ background: BLUE, boxShadow: `0 8px 20px ${BLUE}22` }}
      title={plate}
      aria-label={`License plate ${plate}`}
    >
      <svg
        viewBox="0 0 24 24"
        className="h-4 w-4 shrink-0"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        aria-hidden
      >
        <path d="M4 13l1.4-4.2A2 2 0 0 1 7.3 7h9.4a2 2 0 0 1 1.9 1.8L20 13" />
        <path
          d="M3 13h18v4a1 1 0 0 1-1 1h-1.5a1 1 0 0 1-1-1v-1H6.5v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4z"
          fill="currentColor"
        />
      </svg>
      <span
        className="font-mono tracking-wider"
        style={{ fontSize: "clamp(12px, 1.5vw, 14px)" }}
      >
        {plate}
      </span>
    </div>
  );
}

function ymdInTimeZone(tz, date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const m = Object.fromEntries(
    parts.filter((p) => p.type !== "literal").map((p) => [p.type, p.value]),
  );
  return { y: Number(m.year), mo: Number(m.month), d: Number(m.day) };
}

function dayRangeInTimeZone(tz) {
  // "today" according to tz (not viewer)
  const { y, mo, d } = ymdInTimeZone(tz, new Date());

  const start = makeDateInTimeZone(tz, y, mo, d, 0, 0, 0);

  // tomorrow local midnight (safe across DST)
  const plus36h = new Date(start.getTime() + 36 * 60 * 60 * 1000);
  const t = ymdInTimeZone(tz, plus36h);
  const end = makeDateInTimeZone(tz, t.y, t.mo, t.d, 0, 0, 0);

  return { start, end };
}

function Chip({ children, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-50 text-slate-700 ring-slate-100",
    blue: "bg-sky-50 text-sky-700 ring-sky-100",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    amber: "bg-amber-50 text-amber-700 ring-amber-100",
    rose: "bg-rose-50 text-rose-700 ring-rose-100",
    red: "bg-red-50 text-red-700 ring-red-100",
  };
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium leading-none ring-1 ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

/* Map ONE result row to a card shape */
function mapRowToCard(row, source = "SpotHero", status = "active") {
  return {
    facilityId: "",
    facilityName: row.facility_title || "Facility",
    licensePlate: row.license_plate || "",
    startsAt: row.starts,
    endsAt: row.ends,
    status: (status || "active").toLowerCase(),
    source,
    tz: row.facility_timezone || undefined,
    customer: {
      name: row.renter_full_name || "—",
      phone: row.renter_phone_number || "",
    },
  };
}

function DntOnlyCard({ dnt }) {
  const { s, e, permanent } = dntRange(dnt);

  const sDate = s ? s.toLocaleDateString() : "";
  const sTime = s
    ? s.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "";

  const eDate = e ? e.toLocaleDateString() : "";
  const eTime = e
    ? e.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "";

  return (
    <article className="mt-4 rounded-2xl border border-black/[0.06] bg-white/80 backdrop-blur p-4 sm:p-5 shadow-sm relative">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="grid h-9 w-9 sm:h-10 sm:w-10 place-items-center rounded-xl text-white shadow shrink-0"
            style={{
              background: `linear-gradient(135deg, ${BLUE}, ${BLUE_LIGHT})`,
              boxShadow: `0 8px 16px ${BLUE}20`,
            }}
          >
            <svg
              viewBox="0 0 24 24"
              className="h-4.5 w-4.5 sm:h-5 sm:w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M4 13l1.4-4.2A2 2 0 0 1 7.3 7h9.4a2 2 0 0 1 1.9 1.8L20 13" />
              <path
                d="M3 13h18v4a1 1 0 0 1-1 1h-1.5a1 1 0 0 1-1-1v-1H6.5v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4z"
                fill="currentColor"
              />
            </svg>
          </div>
          <div className="min-w-0">
            <div className="text-[11px] sm:text-xs leading-4 text-slate-500">
              Facility
            </div>
            <div className="truncate text-sm sm:text-base font-semibold leading-6 tracking-tight text-slate-800">
              {dnt.facility_name}
            </div>
          </div>
        </div>

        {/* Big DO NOT TOW badge */}
        <div className="flex items-center gap-2 shrink-0">
          <DoNotTowBadge />
        </div>
      </div>

      {/* times + plate */}
      <div
        className="
          mt-4 grid grid-cols-1 gap-3
          sm:grid-cols-2
          md:grid-cols-[1fr,1fr,220px]
          xl:grid-cols-[1fr,1fr,240px]
        "
      >
        <div className="rounded-xl border border-black/[0.06] p-3 min-w-0">
          <div className="text-[11px] sm:text-xs leading-4 text-slate-500">
            Start time
          </div>
          <div className="mt-1 text-sm font-medium leading-5 text-slate-800">
            {sDate}
          </div>
          <div className="text-sm leading-5 text-slate-700">{sTime}</div>
        </div>

        <div className="rounded-xl border border-black/[0.06] p-3 min-w-0">
          {permanent ? (
            <>
              <div className="text-[11px] sm:text-xs leading-4 text-indigo-600">
                Authorization
              </div>
              <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-900">
                <svg
                  viewBox="0 0 24 24"
                  className="h-3 w-3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden
                >
                  <path d="M12 3v18M4.5 9.5h15" strokeLinecap="round" />
                </svg>
                Permanent
              </div>
              <div className="mt-2 text-xs leading-5 text-slate-500">
                No expiration date
              </div>
            </>
          ) : (
            <>
              <div className="text-[11px] sm:text-xs leading-4 text-slate-500">
                End time
              </div>
              <div className="mt-1 text-sm font-medium leading-5 text-slate-800">
                {eDate}
              </div>
              <div className="text-sm leading-5 text-slate-700">{eTime}</div>
            </>
          )}
        </div>

        <div className="rounded-xl border border-black/[0.06] p-3 min-w-0 overflow-hidden sm:col-span-2 md:col-span-1">
          <div className="mb-2 text-[11px] sm:text-xs leading-4 text-slate-500">
            License Plate
          </div>
          <div className="flex w-full justify-start min-w-0">
            <div className="min-w-0">
              <PlatePillSmall plate={normalizePlate(dnt.license_plate)} />
            </div>
          </div>
        </div>
      </div>

      {/* Relocated facility info */}
      {dnt.is_relocated && dnt.relocated_facility_name && (
        <div className="mt-3 relative rounded-xl border border-amber-200 bg-amber-50 p-3">
          <div className="absolute right-2 top-2">
            <DoNotTowBadge />
          </div>
          <div className="mb-1 pr-28 text-xs font-semibold uppercase tracking-wide text-amber-900">
            Relocated Facility
          </div>
          <div className="text-sm text-amber-900">
            <span className="font-medium">{dnt.relocated_facility_name}</span>
            {(() => {
              const { s, e } = dntRange(dnt);
              const sTime = s
                ? s.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "";
              const eTime = e
                ? e.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "";

              return s && e ? (
                <span className="ml-1">
                  ({s.toLocaleDateString()} {sTime} → {e.toLocaleDateString()}{" "}
                  {eTime})
                </span>
              ) : null;
            })()}
          </div>
          {(dnt.requester_name || dnt.requester_contact) && (
            <div className="mt-1 text-xs text-amber-900/90">
              {dnt.requester_name ? (
                <span className="mr-2">By: {dnt.requester_name}</span>
              ) : null}
              {dnt.requester_contact ? (
                <span className="mr-2">Contact: {dnt.requester_contact}</span>
              ) : null}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

/* --- Icon: Ban --- */
function BanIcon(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      {...props}
    >
      <circle cx="12" cy="12" r="8" />
      <path d="M8.5 8.5l7 7" />
    </svg>
  );
}

/* --- Badge: Do Not Tow --- */
function DoNotTowBadge() {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold tracking-wide text-white shadow
                 ring-1 ring-rose-500/30"
      style={{
        background:
          "linear-gradient(135deg, rgba(244,63,94,1), rgba(225,29,72,1))",
        boxShadow: "0 8px 16px rgba(225,29,72,0.22)",
      }}
      aria-label="Do not tow"
    >
      <BanIcon className="h-3.5 w-3.5" />
      DO NOT TOW
    </span>
  );
}

function AuthorizationBlock({ dnt, viewerTz }) {
  if (!dnt) return null;
  const { s, e, permanent } = dntRange(dnt);
  const tz = viewerTz;

  if (permanent) {
    const startParts = s ? fmtPartsTZ(s.toISOString(), tz) : null;
    return (
      <div className="mt-3 rounded-xl border border-indigo-200/70 bg-gradient-to-br from-indigo-50/90 via-white to-rose-50/50 p-3.5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
              Authorized from
            </div>
            <div className="mt-1 text-sm font-semibold text-slate-900">
              {startParts ? (
                <>
                  {startParts.date}
                  <span className="mx-1.5 font-normal text-slate-400">·</span>
                  <span className="font-medium text-slate-700">{startParts.time}</span>
                </>
              ) : (
                "—"
              )}
            </div>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-indigo-200 bg-white px-3 py-1.5 text-xs font-bold text-indigo-900 shadow-sm">
            <svg
              viewBox="0 0 24 24"
              className="h-3.5 w-3.5 text-indigo-600"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden
            >
              <path d="M12 3v18M4.5 9.5h15" strokeLinecap="round" />
            </svg>
            Permanent
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50/80 p-3.5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 text-sm">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-wide text-rose-600/90">
            Start
          </div>
          <div className="mt-1 font-medium text-rose-950">
            {s ? (
              <>
                {fmtPartsTZ(s.toISOString(), tz).date}{" "}
                <span className="font-normal text-rose-800">
                  {fmtPartsTZ(s.toISOString(), tz).time}
                </span>
              </>
            ) : (
              "—"
            )}
          </div>
        </div>
        <div>
          <div className="text-[11px] font-medium uppercase tracking-wide text-rose-600/90">
            End
          </div>
          <div className="mt-1 font-medium text-rose-950">
            {e ? (
              <>
                {fmtPartsTZ(e.toISOString(), tz).date}{" "}
                <span className="font-normal text-rose-800">
                  {fmtPartsTZ(e.toISOString(), tz).time}
                </span>
              </>
            ) : (
              "—"
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function RelocatedFacilityBlock({ dnt }) {
  if (!dnt) return null;

  const { s, e } = dntRange(dnt);

  const sTxt = s
    ? s.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "";
  const eTxt = e
    ? e.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "";

  return (
    <div className="mt-3 relative rounded-xl border border-amber-200 bg-amber-50 p-3">
      <div className="absolute right-2 top-2">
        <DoNotTowBadge />
      </div>

      <div className="mb-1 pr-28 text-xs font-semibold uppercase tracking-wide text-amber-900">
        Relocated Facility
      </div>

      <div className="text-sm text-amber-900">
        <span className="font-medium">{dnt.relocated_facility_name}</span>
        {s && e ? (
          <span className="ml-1">
            ({s.toLocaleDateString()} {sTxt} → {e.toLocaleDateString()} {eTxt})
          </span>
        ) : null}
      </div>

      {(dnt.requester_name || dnt.requester_contact || dnt.reason) && (
        <div className="mt-1 text-xs text-amber-900/90">
          {dnt.requester_name ? (
            <span className="mr-2">By: {dnt.requester_name}</span>
          ) : null}
          {dnt.requester_contact ? (
            <span className="mr-2">Contact: {dnt.requester_contact}</span>
          ) : null}
        </div>
      )}
    </div>
  );
}

/* --- Reservation card --- */
function ReservationCard({ card, dntMatch, viewerTz }) {
  const tzToShow = card.tz || viewerTz;
  const dntPermanent = dntMatch ? dntRange(dntMatch).permanent : false;

  return (
    <article className="mt-4 rounded-2xl border border-black/[0.06] bg-white/80 backdrop-blur p-4 sm:p-5 shadow-sm">
      {/* header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="grid h-9 w-9 sm:h-10 sm:w-10 place-items-center rounded-xl text-white shadow shrink-0"
            style={{
              background: `linear-gradient(135deg, ${BLUE}, ${BLUE_LIGHT})`,
              boxShadow: `0 8px 16px ${BLUE}20`,
            }}
          >
            <svg
              viewBox="0 0 24 24"
              className="h-4.5 w-4.5 sm:h-5 sm:w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M4 13l1.4-4.2A2 2 0 0 1 7.3 7h9.4a2 2 0 0 1 1.9 1.8L20 13" />
              <path
                d="M3 13h18v4a1 1 0 0 1-1 1h-1.5a1 1 0 0 1-1-1v-1H6.5v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4z"
                fill="currentColor"
              />
            </svg>
          </div>
          <div className="min-w-0">
            <div className="text-[11px] sm:text-xs leading-4 text-slate-500">
              Facility
            </div>
            <div className="truncate text-sm sm:text-base font-semibold leading-6 tracking-tight text-slate-800">
              {card.facilityName}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <StatusBadge status={card.status} />
          {dntMatch ? <DoNotTowBadge /> : null}
        </div>
      </div>

      {/* times + plate */}
      <div
        className={clsx(
          "mt-4 grid grid-cols-1 gap-3",
          dntPermanent
            ? "sm:grid-cols-2 md:grid-cols-[1fr,220px] xl:grid-cols-[1fr,240px]"
            : "sm:grid-cols-2 md:grid-cols-[1fr,1fr,220px] xl:grid-cols-[1fr,1fr,240px]",
        )}
      >
        <div className="rounded-xl border border-black/[0.06] p-3 min-w-0">
          <div className="text-[11px] sm:text-xs leading-4 text-slate-500">
            Start time
          </div>
          <div className="mt-1 text-sm font-medium leading-5 text-slate-800">
            {fmtPartsTZ(card.startsAt, tzToShow).date}
          </div>
          <div className="text-sm leading-5 text-slate-700">
            {fmtPartsTZ(card.startsAt, tzToShow).time}
          </div>
        </div>

        {!dntPermanent && (
          <div className="rounded-xl border border-black/[0.06] p-3 min-w-0">
            <div className="text-[11px] sm:text-xs leading-4 text-slate-500">
              End time
            </div>
            <div className="mt-1 text-sm font-medium leading-5 text-slate-800">
              {fmtPartsTZ(card.endsAt, tzToShow).date}
            </div>
            <div className="text-sm leading-5 text-slate-700">
              {fmtPartsTZ(card.endsAt, tzToShow).time}
            </div>
          </div>
        )}

        <div
          className={clsx(
            "rounded-xl border border-black/[0.06] p-3 min-w-0 overflow-hidden",
            !dntPermanent && "sm:col-span-2 md:col-span-1",
          )}
        >
          <div className="mb-2 text-[11px] sm:text-xs leading-4 text-slate-500">
            License Plate
          </div>
          <div className="flex w-full justify-start min-w-0">
            <div className="min-w-0">
              {card.licensePlate ? (
                <PlatePillSmall plate={normalizePlate(card.licensePlate)} />
              ) : (
                <PlatePillMissing />
              )}
            </div>
          </div>
        </div>
      </div>

      <AuthorizationBlock dnt={dntMatch} viewerTz={tzToShow} />

      {/* Relocated Facility (if matches) */}
      <RelocatedFacilityBlock dnt={dntMatch?.is_relocated ? dntMatch : null} />
    </article>
  );
}

function EmptyStateCard({
  mode = "plate", // "plate" | "title"
  title,
  subtitle,
  onClear,
}) {
  const isTitle = mode === "title";

  const defaultTitle = isTitle
    ? "No active reservations for this facility"
    : "No reservations found for this plate";

  const defaultSubtitle = isTitle
    ? "Try a shorter facility title (street number/name) or select from suggestions."
    : "Double-check spacing/characters, or try a shorter plate format.";

  const tips = isTitle
    ? [
        "Tip: search by street number (e.g. “4909”)",
        "Tip: try street name only (e.g. “Kenmore”)",
      ]
    : ["Tip: remove spaces (e.g. “3KKB36”)", "Tip: check O vs 0 and I vs 1"];

  return (
    <div className="relative overflow-hidden rounded-3xl bg-white ring-1 ring-black/10 shadow-[0_28px_80px_rgba(0,0,0,0.14)]">
      {/* top accent line */}
      <div className="h-1.5 w-full bg-gradient-to-r from-[#2A7EFA] via-black to-[#5AA9FF]" />

      {/* subtle background glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full blur-3xl"
        style={{ background: "rgba(42,126,250,0.18)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-24 -left-24 h-56 w-56 rounded-full blur-3xl"
        style={{ background: "rgba(0,0,0,0.10)" }}
      />

      <div className="relative p-4 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          {/* left */}
          <div className="flex items-start gap-4 min-w-0">
            {/* icon */}
            <div
              className="
                grid h-12 w-12 shrink-0 place-items-center rounded-2xl text-white
                shadow-[0_18px_45px_rgba(42,126,250,0.20)]
              "
              style={{
                background:
                  "linear-gradient(135deg, rgba(42,126,250,1), rgba(15,23,42,1))",
              }}
              aria-hidden
            >
              {isTitle ? (
                // location / facility icon
                <svg
                  viewBox="0 0 24 24"
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 21s7-4.4 7-11a7 7 0 1 0-14 0c0 6.6 7 11 7 11Z" />
                  <path d="M12 10.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" />
                </svg>
              ) : (
                // plate / search icon
                <svg
                  viewBox="0 0 24 24"
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M4 13l1.4-4.2A2 2 0 0 1 7.3 7h9.4a2 2 0 0 1 1.9 1.8L20 13" />
                  <path d="M3 13h18v4a1 1 0 0 1-1 1h-1.5a1 1 0 0 1-1-1v-1H6.5v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4z" />
                  <path d="M8 17h.01M16 17h.01" />
                </svg>
              )}
            </div>

            {/* text */}
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <div className="text-base sm:text-lg font-semibold text-slate-900">
                  {title || defaultTitle}
                </div>

                {/* small mode chip */}
                <span
                  className="
                    inline-flex items-center rounded-full px-2.5 py-1
                    text-[11px] font-semibold
                    ring-1 ring-black/10
                    bg-white
                  "
                >
                  <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-[#2A7EFA]" />
                  {isTitle ? "Title search" : "Plate search"}
                </span>
              </div>

              <div className="mt-1 text-sm text-slate-600 leading-6">
                {subtitle || defaultSubtitle}
              </div>

              {/* tips */}
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {tips.map((t) => (
                  <span
                    key={t}
                    className="
                      inline-flex items-center gap-2
                      rounded-full bg-white px-3 py-1.5
                      text-[12px] text-slate-600
                      ring-1 ring-black/10
                    "
                  >
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#2A7EFA]" />
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* right button */}
          {onClear && (
            <button
              type="button"
              onClick={onClear}
              className="
                w-full sm:w-auto
                rounded-2xl px-4 py-2.5
                text-sm font-semibold text-white
                shadow-[0_16px_36px_rgba(42,126,250,0.25)]
                hover:opacity-95 active:opacity-90
              "
              style={{
                background:
                  "linear-gradient(135deg, rgba(42,126,250,1), rgba(90,169,255,1))",
              }}
            >
              Clear search
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function overlapToday(resStart, resEnd, dayStart, dayEnd) {
  if (!resStart || !resEnd || !dayStart || !dayEnd) return false;
  // include resEnd == dayStart (<=), but still exclude resStart == dayEnd (<)
  return resStart < dayEnd && dayStart <= resEnd;
}
function LookupResult({
  loading,
  slow,
  error,
  data,
  plate,
  onRetry,
  dntItems,
  searchBy,
  searchValue,
  onClear,
}) {
  const PAGE_SIZE = 10;
  const [page, setPage] = React.useState(1);

  // Source rows
  // Source rows – handle both shapes (array under `results` OR single object)
  let rawRows = [];
  const resv = data?.reservation;

  if (Array.isArray(resv?.results)) {
    rawRows = resv.results;
  } else if (Array.isArray(resv)) {
    rawRows = resv;
  } else if (resv && typeof resv === "object") {
    const looksLikeRow =
      "reservation_id" in resv ||
      "display_id" in resv ||
      "license_plate" in resv ||
      "starts" in resv ||
      "facility_title" in resv;
    rawRows = looksLikeRow ? [resv] : [];
  }

  const viewerTz = getViewerTimeZone();
  const { start: todayStart, end: todayEnd } = todayRangeInTimeZone(viewerTz);

  const nowMs = Date.now();

  const rows = rawRows.filter((r) => {
    const tz = r?.facility_timezone || "UTC";

    const s = parseReservationInstant(r?.starts, tz);
    const e = parseReservationInstant(r?.ends, tz);
    if (!s || !e) return false;

    const endPlus = new Date(e.getTime() + END_GRACE_MS);

    // too old
    if (nowMs > endPlus.getTime()) return false;

    // if it hasn't started yet, only allow it if it starts TODAY in the facility timezone
    if (nowMs < s.getTime()) {
      const { start: dayStart, end: dayEnd } = dayRangeInTimeZone(tz);
      const startsToday = s >= dayStart && s < dayEnd;
      if (!startsToday) return false;
    }

    // title mode: only things that "count for today" in facility timezone
    if (searchBy === "title") {
      const { start, end } = dayRangeInTimeZone(tz);
      if (!overlapToday(s, endPlus, start, end)) return false;
    }

    return true;
  });

  // Facility TZ to use when DNT entries don't carry tz (fallback to first row tz if available)
  const fallbackTz = rawRows[0]?.facility_timezone || "UTC";

  // Title mode => keep only reservations ACTIVE today (range overlap)

  const count = rows.length;
  const cards = rows.map((r) => {
    const tz = r?.facility_timezone || "UTC";
    const c = mapRowToCard(r, "SpotHero", "active");

    const e = parseReservationInstant(r?.ends, tz); // ✅ consistent
    const st = statusWithGrace(e);
    c.status = st === "hidden" ? "expired" : st;

    return c;
  });

  // --- DNT logic ---
  const allDnt = Array.isArray(dntItems) ? dntItems : [];
  const visibleDnt = allDnt.filter((d) => {
    if (d?.is_permanent) {
      const { s } = dntRange(d);
      return !s || nowMs >= s.getTime();
    }
    const { e } = dntRange(d);
    if (!e) return false;
    return nowMs <= e.getTime() + END_GRACE_MS;
  });
  const isTitleMode = searchBy === "title";

  // Plate-mode DNT
  const plateNormSearch =
    searchBy === "plate" ? normalizePlate(searchValue) : "";
  const dntForPlate =
    searchBy === "plate"
      ? visibleDnt.filter(
          (d) => normalizePlate(d.license_plate) === plateNormSearch,
        )
      : [];

  // Facility-mode DNT
  const facilityNormSearch = normalizeFacility(searchValue || "");
  const dntForFacility = visibleDnt.filter((d) => {
    const facFields = [
      d?.facility_name,
      d?.facility_title,
      d?.facility,
      ...(isTitleMode ? [d?.relocated_facility_name] : []),
    ];

    return facFields
      .map((s) => normalizeFacility(s || ""))
      .some((facNorm) => {
        if (!facNorm || !facilityNormSearch) return false;
        return (
          facNorm === facilityNormSearch ||
          facNorm.includes(facilityNormSearch) ||
          facilityNormSearch.includes(facNorm)
        );
      });
  });

  // TODAY filter for DNT in title mode (currently same list)
  const dntForFacilityToday = dntForFacility;
  const hasResponse = data != null;
  function findDntMatchForCard(card) {
    if (!visibleDnt.length) return null;
    const plateNorm = normalizePlate(card.licensePlate);
    const facilityName = normalizeFacility(card.facilityName);
    const startL = parseIsoSafe(card.startsAt);
    const endL = parseIsoSafe(card.endsAt);

    return (
      visibleDnt.find((it) => {
        const plateOk = normalizePlate(it?.license_plate) === plateNorm;
        const facilityOk = [
          it?.facility_name,
          it?.facility_title,
          it?.facility,
          it?.relocated_facility_name, // ← consider relocated destination
        ]
          .map((s) => normalizeFacility(s || ""))
          .some((f) => f && f === facilityName);

        const { s: sD, e: eD, permanent } = dntRange(it);
        const endAdj = endL ? new Date(endL.getTime() + 1) : null;
        const timeOk = permanent
          ? !sD || nowMs >= sD.getTime()
          : overlap(startL, endAdj, sD, eD);

        return plateOk && facilityOk && timeOk;
      }) || null
    );
  }
  // Avoid duplicating DNT rows that already got attached to a reservation card
  const attachedDntIds = new Set();
  cards.forEach((c) => {
    const match = findDntMatchForCard(c);
    if (match?.id != null) attachedDntIds.add(match.id);
  });
  const unmatchedDntForFacilityToday = dntForFacilityToday.filter(
    (d) => !attachedDntIds.has(d.id),
  );

  // Standalone DNT (these are the ones you actually show as DNT-only cards)
  const dntStandalone = isTitleMode
    ? unmatchedDntForFacilityToday
    : dntForPlate;

  // Counts must reflect what you show
  const dntCount = dntStandalone.length;
  const totalCount = count + dntCount;

  // Combined list for pagination
  const combinedItems = [
    ...cards.map((c) => ({ type: "res", card: c })),
    ...dntStandalone.map((d) => ({ type: "dnt", dnt: d })),
  ];

  const totalDisplayCount = combinedItems.length;
  React.useEffect(() => {
    setPage(1);
  }, [plate, totalDisplayCount]);

  // Pagination based on combined list
  const pageCount = Math.max(1, Math.ceil(totalDisplayCount / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const end = Math.min(start + PAGE_SIZE, totalDisplayCount);
  const pageItems = combinedItems.slice(start, end);

  return (
    <div className="mt-4">
      {/* header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm text-slate-600 truncate">
            Lookup result for
          </span>
          <span
            className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium text-white"
            style={{ background: BLUE }}
            title={plate}
          >
            {plate}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {loading && <Chip tone="blue">Searching…</Chip>}

          {!loading && !error && (
            <>
              {/* TOTAL (green) */}
              {totalCount > 0 ? (
                <Chip tone="green">Total • {totalCount}</Chip>
              ) : (
                <Chip tone="red">No matches found</Chip>
              )}

              {/* Reservations count */}
              {count > 0 && <Chip tone="blue">Reservations • {count}</Chip>}

              {/* DNT count */}
              {dntCount > 0 && <Chip tone="rose">Do Not Tow • {dntCount}</Chip>}
            </>
          )}
        </div>
      </div>
      {/* error */}
      {!loading && error && (
        <div className="mt-3 flex items-start justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          <div className="min-w-0">{error}</div>
          <button
            onClick={onRetry}
            className="shrink-0 rounded-md bg-rose-600 px-3 py-1 text-xs font-semibold text-white hover:shadow"
          >
            Retry
          </button>
        </div>
      )}
      {/* loading */}
      {loading && (
        <div className="mt-4 grid place-items-center rounded-2xl border border-black/[0.06] bg-white p-10">
          <Loader size="lg" label={`Searching ${plate}…`} />
        </div>
      )}
      {/* reservations exist -> paginated list */}
      {/* Combined paginated list: Reservations + DNT */}
      {!loading && !error && totalDisplayCount > 0 && (
        <>
          <div className="grid gap-4">
            {pageItems.map((it, idx) => {
              if (it.type === "res") {
                const c = it.card;
                return (
                  <ReservationCard
                    key={`res-${start + idx}-${c.licensePlate}-${c.startsAt}`}
                    card={c}
                    dntMatch={findDntMatchForCard(c)}
                    viewerTz={viewerTz}
                  />
                );
              }
              const d = it.dnt;
              return <DntOnlyCard key={`dnt-${d.id ?? start + idx}`} dnt={d} />;
            })}
          </div>

          {/* Pager (based on combined total) */}
          <div className="mt-4 flex flex-col items-center justify-between gap-3 sm:flex-row">
            <div className="text-sm text-slate-600">
              Showing{" "}
              <span className="font-medium text-slate-900">
                {start + 1}–{end}
              </span>{" "}
              of{" "}
              <span className="font-medium text-slate-900">
                {totalDisplayCount}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="rounded-lg border border-black/[0.08] px-3 py-2 text-sm disabled:opacity-50 hover:bg-black/[0.03]"
              >
                Prev
              </button>

              <span className="text-sm text-slate-700">
                Page <span className="font-medium">{page}</span> / {pageCount}
              </span>

              <button
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                disabled={page >= pageCount}
                className="rounded-lg border border-black/[0.08] px-3 py-2 text-sm disabled:opacity-50 hover:bg-black/[0.03]"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
      {/* Empty state */}

      {hasResponse && !loading && !error && totalDisplayCount === 0 && (
        <>
          <div className="mt-4">
            <EmptyStateCard
              mode={searchBy === "title" ? "title" : "plate"}
              onClear={onClear} // pass from Overview if you want
            />
          </div>

          {dntCount > 0 && (
            <div className="mt-4 grid gap-4">
              {dntStandalone.map((d) => (
                <DntOnlyCard key={`dnt-${d.id}`} dnt={d} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function highlightMatch(text, q) {
  const t = String(text || "");
  const s = String(q || "").trim();
  if (!s) return t;
  const i = t.toLowerCase().indexOf(s.toLowerCase());
  if (i < 0) return t;
  const a = t.slice(0, i);
  const b = t.slice(i, i + s.length);
  const c = t.slice(i + s.length);
  return (
    <>
      {a}
      <span className="font-semibold text-slate-900">{b}</span>
      {c}
    </>
  );
}

export default function Overview() {
  const [query, setQuery] = useState("");
  const [searchPlate, setSearchPlate] = useState("");
  const [lookLoading, setLookLoading] = useState(false);
  const [lookSlow, setLookSlow] = useState(false);
  const [lookData, setLookData] = useState(null);
  const [dntItems, setDntItems] = useState([]);
  const [lookError, setLookError] = useState("");
  const [searchBy, setSearchBy] = useState("plate"); // 'plate' | 'title'
  const [searchToken, setSearchToken] = useState({ by: "", value: "" });
  const lookupAbortRef = useRef(null);
  const dntAbortRef = useRef(null);
  const inputRef = useRef(null);
  const showImage = !searchToken.value;
  const [titleSuggestions, setTitleSuggestions] = useState([]);
  const [titleSuggestOpen, setTitleSuggestOpen] = useState(false);
  const [titleSuggestLoading, setTitleSuggestLoading] = useState(false);
  const titleSuggestAbortRef = useRef(null);

  // ✅ wrapper ref for click-outside behavior
  const inputWrapRef = useRef(null);

  // ✅ one function to fully close + stop suggestion UI
  function closeTitleSuggest() {
    try {
      if (titleSuggestAbortRef.current) {
        titleSuggestAbortRef.current.abort();
        titleSuggestAbortRef.current = null;
      }
    } catch {}

    setTitleSuggestOpen(false);
    setTitleSuggestLoading(false);
    setTitleSuggestions([]);
  }

  const facilityImages = lookData?.facility_images || null;

  const abortAll = () => {
    if (lookupAbortRef.current) {
      lookupAbortRef.current.abort();
      lookupAbortRef.current = null;
    }
    if (dntAbortRef.current) {
      dntAbortRef.current.abort();
      dntAbortRef.current = null;
    }
  };
  function triggerSearch(by, rawValue) {
    const val =
      by === "plate" ? normalizePlate(rawValue) : String(rawValue || "").trim();

    // close dropdown instantly
    if (by === "title") closeTitleSuggest();

    // ✅ prevent empty-state flash: make UI "loading" NOW (before useEffect runs)
    setLookLoading(true);
    setLookSlow(false);
    setLookError("");
    setLookData(null);
    setDntItems([]);

    // sidebar behavior
    if (by === "plate") emitSidebarPlate(val);
    else emitSidebarPlate(null);

    // finally: trigger lookup effect
    setSearchToken({ by, value: val });
  }
  function handleSearch() {
    triggerSearch(searchBy, query);
  }

  const switchMode = (next) => {
    if (next === searchBy) return;
    abortAll();
    closeTitleSuggest();
    setSearchBy(next);
    setQuery("");
    setSearchToken({ by: "", value: "" });
    setLookData(null);
    setLookError("");
    setDntItems([]);
    setLookLoading(false);
    setLookSlow(false);
    if (searchBy === "title") {
      emitFacilityImages(null);
      emitSidebarImagesLoading(false);
    }
    if (searchBy === "plate") {
      emitSidebarPlate(null); // clear plate preview when leaving plate mode
    }
    requestAnimationFrame(() => inputRef.current?.focus());
  };
  async function performLookup(by, value) {
    // cancel in-flight
    if (lookupAbortRef.current) {
      lookupAbortRef.current.abort();
      lookupAbortRef.current = null;
    }
    if (dntAbortRef.current) {
      dntAbortRef.current.abort();
      dntAbortRef.current = null;
    }

    setLookError("");
    setLookData(null);
    setDntItems([]);

    // Title mode → clear old images + show loader immediately
    if (by === "title") {
      emitFacilityImages(null);
      emitSidebarImagesLoading(true);
      emitSidebarPlate(null); // hide plate when searching by title
    } else if (by === "plate") {
      emitSidebarPlate(value); // show the plate preview
    }

    if (!value) {
      setLookLoading(false);
      setLookSlow(false);
      if (by === "title") emitSidebarImagesLoading(false);
      return;
    }

    const controller = new AbortController();
    lookupAbortRef.current = controller;

    setLookLoading(true);
    setLookSlow(false);
    const slowTimer = setTimeout(() => setLookSlow(true), 1200);

    try {
      const res = await lookup(
        by === "title"
          ? { search_type: "title", title: value, plate_number: "_" }
          : { search_type: "license_plate", plate_number: value },
        { signal: controller.signal, timeout: 8000 },
      );

      setLookData(res || {});

      // Only push images to sidebar for Title searches
      if (by === "title") {
        emitFacilityImages(res?.facility_images || null);
      }

      if (!controller.signal.aborted) {
        const dntCtl = new AbortController();
        dntAbortRef.current = dntCtl;

        const dntParams =
          by === "plate"
            ? { active_only: true, limit: 10000, search: String(value).trim() }
            : { active_only: true, limit: 10000 }; // Title → broad; filter client-side

        const dntBody = await listDoNotTow(dntParams, {
          signal: dntCtl.signal,
          timeout: 15000,
        }).catch(() => null);

        if (!dntCtl.signal.aborted && dntBody) {
          const items = Array.isArray(dntBody)
            ? dntBody
            : dntBody?.results || dntBody?.items || [];
          setDntItems(items);
        }
      }
    } catch (e) {
      if (!controller.signal.aborted) {
        setLookError(
          e?.payload?.message ||
            e?.message ||
            "Lookup failed. Please try again.",
        );
        // ensure sidebar loader stops on error in Title mode
        if (by === "title") emitFacilityImages(null);
      }
    } finally {
      clearTimeout(slowTimer);
      setLookLoading(false);
      if (by === "title") emitSidebarImagesLoading(false);
    }
  }
  useEffect(() => {
    if (!searchToken.value) return;
    performLookup(searchToken.by, searchToken.value);
    return () => {
      if (lookupAbortRef.current) {
        lookupAbortRef.current.abort();
        lookupAbortRef.current = null;
      }
      if (dntAbortRef.current) {
        dntAbortRef.current.abort();
        dntAbortRef.current = null;
      }
    };
  }, [searchToken]);

  useEffect(() => {
    // ✅ Only for Title mode
    if (searchBy !== "title") {
      setTitleSuggestions([]);
      setTitleSuggestOpen(false);
      return;
    }

    const q = String(query || "").trim();
    if (!q) {
      setTitleSuggestions([]);
      setTitleSuggestOpen(false);
      return;
    }

    // debounce typing
    const t = setTimeout(async () => {
      // cancel in-flight
      if (titleSuggestAbortRef.current) {
        titleSuggestAbortRef.current.abort();
        titleSuggestAbortRef.current = null;
      }

      const ctl = new AbortController();
      titleSuggestAbortRef.current = ctl;

      setTitleSuggestLoading(true);
      try {
        const res = await searchFacilityTitles(
          { q, limit: 10 },
          { signal: ctl.signal, timeout: 15000 },
        );

        if (!ctl.signal.aborted) {
          const items = Array.isArray(res?.results) ? res.results : [];
          setTitleSuggestions(items);
          setTitleSuggestOpen(true);
        }
      } catch {
        if (!ctl.signal.aborted) {
          setTitleSuggestions([]);
          setTitleSuggestOpen(false);
        }
      } finally {
        if (!ctl.signal.aborted) setTitleSuggestLoading(false);
      }
    }, 250);

    return () => clearTimeout(t);
  }, [query, searchBy]);

  useEffect(() => {
    // once results arrive, close dropdown (title mode)
    if (searchBy === "title" && lookData) {
      closeTitleSuggest();
    }
    // also close on error (optional but recommended)
    if (searchBy === "title" && lookError) {
      closeTitleSuggest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookData, lookError, searchBy]);
  useEffect(() => {
    function onDown(e) {
      if (!inputWrapRef.current) return;
      if (!inputWrapRef.current.contains(e.target)) {
        closeTitleSuggest();
      }
    }
    document.addEventListener("mousedown", onDown, true);
    return () => document.removeEventListener("mousedown", onDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleClear() {
    closeTitleSuggest();
    setQuery("");
    setSearchToken({ by: "", value: "" });
    setLookData(null);
    setLookError("");
    setDntItems([]);
    setLookLoading(false); // ✅ important
    setLookSlow(false);

    emitSidebarPlate(null);
    if (searchBy === "title") {
      emitFacilityImages(null);
      emitSidebarImagesLoading(false);
    }
  }

  // --- render ---
  return (
    <div className="mx-auto w-full max-w-6xl space-y-5 px-3 py-4 sm:px-4 md:py-6 relative isolate">
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
          Valid Reservations
        </h1>
      </header>

      {/* Top bar with search mode toggle */}
      <div className="relative z-[60] rounded-2xl border border-black/[0.06] bg-white/70 backdrop-blur px-4 py-3 shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
          {/* Mode toggle */}
          <div className="flex shrink-0 items-center gap-1 rounded-xl border border-black/[0.06] p-1">
            <button
              type="button"
              onClick={() => switchMode("plate")}
              className={clsx(
                "rounded-lg px-2.5 py-1.5 text-xs sm:text-sm",
                searchBy === "plate"
                  ? "bg-black/[0.05] font-semibold"
                  : "text-black/70 hover:text-black",
              )}
            >
              Plate
            </button>

            <button
              type="button"
              onClick={() => switchMode("title")}
              className={clsx(
                "rounded-lg px-2.5 py-1.5 text-xs sm:text-sm",
                searchBy === "title"
                  ? "bg-black/[0.05] font-semibold"
                  : "text-black/70 hover:text-black",
              )}
            >
              Title
            </button>
          </div>

          {/* Input */}
          <div ref={inputWrapRef} className="relative z-[70] flex-1">
            <input
              ref={inputRef}
              id="lookup-input"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                if (searchBy === "title") {
                  // open only while typing (and not during lookup)
                  const v = String(e.target.value || "").trim();
                  setTitleSuggestOpen(!!v && !lookLoading);
                }
              }}
              onFocus={() => {
                if (
                  searchBy === "title" &&
                  String(query || "").trim() &&
                  !lookLoading
                ) {
                  setTitleSuggestOpen(true);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearch();
                }
                if (e.key === "Escape") {
                  closeTitleSuggest();
                }
              }}
              placeholder={
                searchBy === "plate"
                  ? "Enter license plate (e.g. 3KKB36)"
                  : "Enter facility title (e.g. 4909 N Kenmore Ave)"
              }
              aria-busy={lookLoading ? "true" : "false"}
              className="w-full rounded-xl border border-black/[0.06] bg-white px-4 py-3 text-[15px] leading-5 text-slate-800 outline-none transition focus:ring-4"
              style={{
                ["--blue"]: BLUE,
                boxShadow: "inset 0 0 0 9999px transparent",
                borderColor: "rgba(0,0,0,.06)",
              }}
            />

            {/* ✅ Pro Title dropdown (small text, solid white, responsive) */}
            {searchBy === "title" &&
              titleSuggestOpen &&
              !lookLoading &&
              String(query || "").trim() && (
                <div
                  className="
        absolute left-0 right-0 top-full mt-2 z-[80]
        overflow-hidden rounded-2xl
        border border-black/10
        bg-white
        shadow-[0_24px_70px_rgba(0,0,0,0.18)]
        ring-1 ring-black/5
      "
                  role="listbox"
                  aria-label="Facility suggestions"
                >
                  {/* Header (sticky) */}
                  <div className="sticky top-0 z-10 border-b border-black/10 bg-white">
                    <div className="flex items-center justify-between gap-3 px-3 py-2 sm:px-4 sm:py-2.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <div
                          className="grid h-8 w-8 place-items-center rounded-xl text-white shrink-0"
                          style={{
                            background: `linear-gradient(135deg, ${BLUE}, ${BLUE_LIGHT})`,
                            boxShadow: `0 10px 18px ${BLUE}22`,
                          }}
                        >
                          {/* Search icon */}
                          <svg
                            viewBox="0 0 24 24"
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            aria-hidden
                          >
                            <path d="M10 18a8 8 0 1 1 5.3-14.1A8 8 0 0 1 10 18Z" />
                            <path d="M21 21l-4.3-4.3" />
                          </svg>
                        </div>

                        <div className="min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="text-[10px] font-semibold tracking-[0.20em] text-slate-500">
                              FACILITIES
                            </div>
                            <span className="inline-flex items-center rounded-full bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-600 ring-1 ring-black/10">
                              {titleSuggestions.length || 0}
                            </span>
                          </div>
                          <div className="truncate text-[11px] text-slate-600">
                            Select a facility title
                          </div>
                        </div>
                      </div>

                      {/* right side */}
                      {titleSuggestLoading ? (
                        <div className="shrink-0 inline-flex items-center gap-2 rounded-full bg-slate-50 px-2.5 py-1 text-[11px] text-slate-600 ring-1 ring-black/10">
                          <span
                            className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-black/20 border-t-black/60"
                            aria-hidden
                          />
                          Searching…
                        </div>
                      ) : (
                        <div className="hidden sm:inline-flex items-center gap-1 rounded-full bg-slate-50 px-2.5 py-1 text-[11px] text-slate-600 ring-1 ring-black/10">
                          <span className="font-semibold">Esc</span>
                          <span className="text-slate-500">close</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* List */}
                  <div className="max-h-[min(60vh,22rem)] overflow-auto p-1.5 sm:p-2">
                    {titleSuggestions.length > 0 ? (
                      <div className="space-y-1">
                        {titleSuggestions.map((it) => (
                          <button
                            key={String(it.id ?? it.title)}
                            type="button"
                            role="option"
                            className="
                  group w-full text-left
                  rounded-xl
                  px-3 py-2
                  sm:px-3.5 sm:py-2.5
                  transition
                  hover:bg-slate-50
                  active:bg-slate-100
                  focus:outline-none focus:ring-4 focus:ring-black/10
                "
                            onMouseDown={(e) => e.preventDefault()} // keep focus
                            onClick={() => {
                              const t = String(it.title || "").trim();
                              setQuery(t);
                              triggerSearch("title", t);
                            }}
                          >
                            <div className="flex items-start gap-2.5 sm:gap-3">
                              {/* location pin icon */}
                              <div
                                className="
                      mt-0.5 grid h-8 w-8 sm:h-9 sm:w-9 place-items-center
                      rounded-xl
                      bg-white
                      ring-1 ring-black/10
                      shadow-sm
                      shrink-0
                      group-hover:ring-black/20
                    "
                                aria-hidden
                              >
                                <svg
                                  viewBox="0 0 24 24"
                                  className="h-4 w-4 sm:h-[18px] sm:w-[18px]"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path d="M12 21s7-4.4 7-11a7 7 0 1 0-14 0c0 6.6 7 11 7 11Z" />
                                  <path d="M12 10.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" />
                                </svg>
                              </div>

                              <div className="min-w-0 flex-1">
                                <div className="truncate text-[12px] sm:text-[13px] font-semibold text-slate-900 leading-5">
                                  {highlightMatch(it.title, query)}
                                </div>
                                <div className="mt-0.5 truncate text-[11px] text-slate-500 leading-4">
                                  Tap to search this facility
                                </div>
                              </div>

                              {/* right hint */}
                              <div className="shrink-0 flex items-center gap-2">
                                <span className="text-[11px] text-slate-400 opacity-0 group-hover:opacity-100 transition">
                                  ↵
                                </span>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : !titleSuggestLoading ? (
                      <div className="px-3 py-6 sm:px-4">
                        <div className="flex items-start gap-3">
                          <div className="grid h-9 w-9 place-items-center rounded-xl bg-slate-50 ring-1 ring-black/10 shrink-0">
                            <svg
                              viewBox="0 0 24 24"
                              className="h-4.5 w-4.5 text-slate-500"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              aria-hidden
                            >
                              <path d="M10 18a8 8 0 1 1 5.3-14.1A8 8 0 0 1 10 18Z" />
                              <path d="M21 21l-4.3-4.3" />
                            </svg>
                          </div>
                          <div className="min-w-0">
                            <div className="text-[12px] font-semibold text-slate-800">
                              No matches found
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                              Try a shorter query (street number or street
                              name).
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // small skeleton
                      <div className="p-2 space-y-2">
                        {Array.from({ length: 6 }).map((_, i) => (
                          <div
                            key={i}
                            className="rounded-xl border border-black/10 bg-white px-3 py-2.5"
                          >
                            <div className="flex items-center gap-3">
                              <div className="h-9 w-9 rounded-xl bg-slate-100 animate-pulse" />
                              <div className="flex-1 space-y-2">
                                <div className="h-3 w-3/4 rounded bg-slate-100 animate-pulse" />
                                <div className="h-3 w-1/2 rounded bg-slate-100 animate-pulse" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Footer hint (sticky) */}
                  <div className="sticky bottom-0 border-t border-black/10 bg-white px-3 py-2 sm:px-4">
                    <div className="flex items-center justify-between gap-3 text-[10px] sm:text-[11px] text-slate-500">
                      <span className="truncate">
                        Tip: type “4909” or “Kenmore” to narrow results
                      </span>
                      <span className="hidden sm:inline-flex items-center gap-1">
                        <span className="rounded bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-600 ring-1 ring-black/10">
                          Esc
                        </span>
                        close
                      </span>
                    </div>
                  </div>
                </div>
              )}

            <div
              className={
                "absolute left-0 right-0 -bottom-px h-0.5 rounded-full transition-opacity " +
                (lookLoading ? "bg-[#2A7EFA] opacity-100" : "opacity-0")
              }
            />

            <div
              className="pointer-events-none absolute inset-y-0 right-3 grid place-items-center text-slate-400"
              aria-hidden
            >
              {lookLoading ? (
                <Spinner className="h-5 w-5" />
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <circle cx="11" cy="11" r="7" />
                  <path d="M20 20l-3.5-3.5" />
                </svg>
              )}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex items-stretch gap-2">
            <button
              type="button"
              onClick={handleSearch}
              disabled={lookLoading}
              className="rounded-xl px-3 py-2 text-xs font-semibold text-white shadow transition disabled:opacity-60 sm:px-5 sm:py-3 sm:text-sm"
              style={{
                background: `linear-gradient(135deg, ${BLUE}, ${BLUE_LIGHT})`,
                boxShadow: `0 10px 22px ${BLUE}22`,
              }}
            >
              Search
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="rounded-xl border border-black/[0.08] bg-white px-3 py-2 text-xs font-medium text-slate-700 transition hover:bg-slate-50 sm:px-4 sm:py-3 sm:text-sm"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      {searchToken.value && (
        <LookupResult
          loading={lookLoading}
          slow={lookSlow}
          error={lookError}
          onClear={handleClear}
          data={lookData}
          plate={
            searchBy === "plate"
              ? searchToken.value
              : `Title: ${searchToken.value}`
          }
          searchBy={searchBy}
          searchValue={searchToken.value}
          onRetry={() => performLookup(searchToken.by, searchToken.value)}
          dntItems={dntItems}
        />
      )}

      {/* Background art (mobile) */}
      <AnimatePresence>
        {showImage && (
          <motion.img
            key="towing-mobile"
            src={TowingImage}
            alt=""
            className="md:hidden fixed bottom-[max(env(safe-area-inset-bottom),0px)] right-[max(env(safe-area-inset-right),0px)]
                       w-[min(100vw,860px)] max-h-[62vh] object-contain pointer-events-none select-none -z-10 -mb-[6px] -mr-[40px]"
            style={{ willChange: "transform, opacity" }}
            initial={{ opacity: 0, x: 40, scale: 0.995 }}
            animate={{
              opacity: 0.98,
              x: 0,
              scale: 1,
              transition: { type: "spring", stiffness: 300, damping: 30 },
            }}
            exit={{
              opacity: 0,
              x: 20,
              scale: 0.995,
              transition: { duration: 0.15, ease: "easeOut" },
            }}
          />
        )}
      </AnimatePresence>

      {/* Background art (desktop) */}
      <AnimatePresence>
        {showImage && (
          <motion.img
            key="towing-fixed"
            src={TowingImage}
            alt=""
            className="hidden md:block fixed bottom-[max(env(safe-area-inset-bottom),0px)] right-[max(env(safe-area-inset-right),0px)]
                       w-[min(62vw,1100px)] lg:w-[min(58vw,1200px)] xl:w-[min(54vw,1400px)] 2xl:w-[min(50vw,1600px)]
                       max-h-[76vh] xl:max-h-[82vh] object-contain pointer-events-none select-none -z-10 -mb-[6px] -mr-[40px]"
            style={{ willChange: "transform, opacity" }}
            initial={{ opacity: 0, x: 40, scale: 0.995 }}
            animate={{
              opacity: 0.98,
              x: 0,
              scale: 1,
              transition: { type: "spring", stiffness: 300, damping: 30 },
            }}
            exit={{
              opacity: 0,
              x: 20,
              scale: 0.995,
              transition: { duration: 0.15, ease: "easeOut" },
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
